import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, Spin, Typography, Descriptions, Tag, Button, message, Modal, Input, Divider, Row, Col, Alert, Timeline } from 'antd';
import axios from 'axios';
import moment from 'moment';

const { Title } = Typography;

const OrderGeneral = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const hash = searchParams.get('hash');
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthModalVisible, setIsAuthModalVisible] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('authToken') || '');

  const fetchOrderData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/v1/docs_sales/verify/${hash}?order_id=${id}&role=general`);
      setOrderData(response.data);
      setError(null);
    } catch (err) {
      console.error('Ошибка при загрузке данных заказа:', err);
      setError('Не удалось загрузить данные заказа. Проверьте корректность ссылки.');
    } finally {
      setLoading(false);
    }
  }, [id, hash]);

  useEffect(() => {
    if (id && hash) {
      fetchOrderData();
    }
  }, [id, hash, fetchOrderData]);

  const handleAuthSubmit = () => {
    localStorage.setItem('authToken', token);
    setIsAuthModalVisible(false);
    message.success('Токен сохранен успешно');
  };

  const handleSendNotification = async (type) => {
    try {
      const savedToken = localStorage.getItem('authToken');
      if (!savedToken) {
        message.error('Необходим токен для отправки уведомления');
        setIsAuthModalVisible(true);
        return;
      }

      const response = await axios.post(
        `/api/v1/docs_sales/${id}/notify?token=${savedToken}&type=${encodeURIComponent(type)}&send_notification=true`,
        ''
      );
      
      if (response.data.success) {
        message.success(`Уведомление '${type}' успешно отправлено`);
      } else {
        message.error('Ошибка при отправке уведомления');
      }
    } catch (err) {
      console.error('Ошибка при отправке уведомления:', err);
      message.error('Не удалось отправить уведомление');
    }
  };

  const getStatusTag = (status) => {
    const statusMap = {
      received: { color: 'blue', text: 'Получен' },
      processed: { color: 'purple', text: 'Обработан' },
      collecting: { color: 'orange', text: 'Собирается' },
      collected: { color: 'cyan', text: 'Собран' },
      picked: { color: 'geekblue', text: 'Забран' },
      delivered: { color: 'green', text: 'Доставлен' },
      cancelled: { color: 'red', text: 'Отменён' },
    };

    const defaultStatus = { color: 'default', text: status };
    const currentStatus = statusMap[status] || defaultStatus;

    return <Tag color={currentStatus.color}>{currentStatus.text}</Tag>;
  };

  const parseComments = (comment) => {
    if (!comment) return [];
    
    const lines = comment.split(/\r?\n/);
    
    const commentRegex = /^\[(.+?)\] (.+)$/;
    
    return lines
      .map(line => {
        const match = line.match(commentRegex);
        if (match && match.length === 3) {
          const timestamp = match[1];
          const text = match[2];
          
          const dateTimeParts = timestamp.split(' ');
          if (dateTimeParts.length === 2) {
            const datePart = dateTimeParts[0];
            const timePart = dateTimeParts[1].split('.')[0];
            const dateTimeStr = `${datePart} ${timePart}`;
            
            return {
              timestamp: moment(dateTimeStr, 'YYYY-MM-DD HH:mm:ss'),
              text: text
            };
          }
        }
        return null;
      })
      .filter(item => item !== null)
      .sort((a, b) => b.timestamp.valueOf() - a.timestamp.valueOf());
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    const errorMessage = typeof error === 'string' 
      ? error 
      : error?.message || error?.response?.data?.detail || error?.response?.data?.message || 'Произошла ошибка';
    return (
      <div style={{ padding: '20px' }}>
        <Alert
          message="Ошибка"
          description={errorMessage}
          type="error"
          showIcon
        />
      </div>
    );
  }
  
  const comments = orderData?.comment ? parseComments(orderData.comment) : [];

  return (
    <div style={{ padding: '20px' }}>
      <Card title={<Title level={3}>Заказ #{orderData?.number}</Title>}>
        <Descriptions bordered>
          <Descriptions.Item label="Статус" span={3}>
            {orderData?.order_status && getStatusTag(orderData.order_status)}
          </Descriptions.Item>
          <Descriptions.Item label="ID" span={3}>{orderData?.id}</Descriptions.Item>
          <Descriptions.Item label="Дата создания" span={3}>
            {orderData?.created_at && moment.unix(orderData.created_at).format('DD.MM.YYYY HH:mm')}
          </Descriptions.Item>
          <Descriptions.Item label="Сумма" span={3}>
            {orderData?.sum ? `${orderData.sum.toLocaleString()} руб.` : 'Не указана'}
          </Descriptions.Item>
        </Descriptions>
        
        <Divider />
        
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={8}>
            <Card size="small" title="Статусы сборки и доставки">
              <p>Сборщик: {orderData?.assigned_picker ? `ID ${orderData.assigned_picker}` : 'Не назначен'}</p>
              {orderData?.picker_started_at && (
                <p>Начало сборки: {moment(orderData.picker_started_at).format('DD.MM.YYYY HH:mm')}</p>
              )}
              {orderData?.picker_finished_at && (
                <p>Завершение сборки: {moment(orderData.picker_finished_at).format('DD.MM.YYYY HH:mm')}</p>
              )}
              <p>Курьер: {orderData?.assigned_courier ? `ID ${orderData.assigned_courier}` : 'Не назначен'}</p>
              {orderData?.courier_picked_at && (
                <p>Заказ забран: {moment(orderData.courier_picked_at).format('DD.MM.YYYY HH:mm')}</p>
              )}
              {orderData?.courier_delivered_at && (
                <p>Заказ доставлен: {moment(orderData.courier_delivered_at).format('DD.MM.YYYY HH:mm')}</p>
              )}
            </Card>
          </Col>
          
          <Col xs={24} sm={24} md={16}>
            <Card size="small" title="Отправка уведомлений">
              <div style={{ marginBottom: '10px' }}>
                <Button type="primary" onClick={() => handleSendNotification('Общее')} style={{ marginRight: '10px' }}>
                  Отправить общее уведомление
                </Button>
                <Button onClick={() => handleSendNotification('Сборка')} style={{ marginRight: '10px' }}>
                  Отправить уведомление сборщику
                </Button>
                <Button onClick={() => handleSendNotification('Доставка')}>
                  Отправить уведомление курьеру
                </Button>
              </div>
              <Alert 
                message="Внимание" 
                description="Для отправки уведомлений требуется авторизация с токеном. Нажмите на кнопку 'Авторизоваться' в шапке, если у вас нет доступа." 
                type="info" 
                showIcon 
              />
            </Card>
          </Col>
        </Row>
        
        {comments.length > 0 && (
          <Card title="История изменений" style={{ marginTop: '20px' }}>
            <Timeline mode="left" style={{ maxHeight: '400px', overflowY: 'auto', padding: '10px' }}>
              {comments.map((comment, index) => (
                <Timeline.Item key={index} label={comment.timestamp.format('DD.MM.YYYY HH:mm:ss')}>
                  {comment.text}
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        )}
      </Card>

      <Modal
        title="Авторизация"
        open={isAuthModalVisible}
        onOk={handleAuthSubmit}
        onCancel={() => setIsAuthModalVisible(false)}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <p>Введите ваш токен для доступа к API:</p>
        <Input.Password
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="Введите токен"
        />
        <div style={{ marginTop: '10px' }}>
          <a href="https://t.me/tablecrmbot" target="_blank" rel="noreferrer">
            У вас нет токена? Получите его в Telegram боте @tablecrmbot
          </a>
        </div>
      </Modal>

      <Button 
        type="primary" 
        onClick={() => setIsAuthModalVisible(true)} 
        style={{ position: 'fixed', top: '10px', right: '10px' }}
      >
        Авторизоваться
      </Button>
    </div>
  );
};

export default OrderGeneral; 